import { z } from 'zod';
import { AgentVoiceCanonical } from './settings/agent-settings.js';
/**
 * Personal agent-preferences captured on the profile's "How LeanOpx
 * shows up for you" card. The page-local copy is a small shim around
 * existing closed sets: `voice` reuses the canonical `AgentVoice` enum
 * from settings; `briefingCadence` + `autonomy` are new enums living
 * here because they're only ever read off the personal profile (the
 * org-wide cadence + autonomy schema in `settings/` is the cross-team
 * default — these are the per-user overrides that survive across orgs).
 *
 * `quietHours` is a wall-clock window the briefing scheduler honors;
 * stored as two `HH:MM` strings so DST doesn't shift the window.
 */
export const BriefingCadence = z.enum(['daily', 'weekdays', 'weekly', 'on-change']);
export const AgentAutonomyLevel = z.enum(['suggest-only', 'draft-and-safe-send', 'full-auto']);
const HHMM = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, { message: 'must be HH:MM 24-hour' });
export const QuietHoursWindow = z.object({
    start: HHMM,
    end: HHMM,
});
/**
 * "Where to find you" channels — each row keeps the operator's handle
 * for the channel and a per-channel `enabled` toggle. The agent reads
 * `enabled` before fanning a notification out; the handle/phone/device
 * fields stay best-effort until the device-registration pipeline lands.
 *
 * `slack`/`sms`/`push` are optional because their identity (handle /
 * phone / device id) requires the device-registration pipeline that
 * doesn't ship yet — the row renders a "Not yet wired" affordance when
 * the slot is absent.
 */
export const UserNotificationChannels = z.object({
    email: z.boolean().default(true),
    slack: z
        .object({
        handle: z.string().max(120),
        enabled: z.boolean().default(true),
    })
        .optional(),
    sms: z
        .object({
        phone: z.string().max(40),
        enabled: z.boolean().default(false),
    })
        .optional(),
    push: z
        .object({
        deviceId: z.string().max(200),
        enabled: z.boolean().default(false),
    })
        .optional(),
});
export const UserAgentPreferences = z.object({
    voice: AgentVoiceCanonical.optional(),
    briefingCadence: BriefingCadence.optional(),
    autonomy: AgentAutonomyLevel.optional(),
    quietHours: QuietHoursWindow.optional(),
});
/**
 * Firestore `users/{uid}` profile. Written by the Cloud Functions admin SDK
 * only; clients cannot write (see firestore.rules).
 *
 * `primaryOrgId` + `memberships[]` + `isSuperAdmin` are denormalized
 * mirrors of the custom-claims state, kept in sync by the claim-sync
 * helpers in `apps/functions/src/auth/claims.ts`. `memberships` is a flat
 * list of orgIds the user belongs to (the per-org role lives in the
 * `organizations/{orgId}/members/{uid}` subcollection).
 *
 * The `title` / `timezone` / `phone` / `location` fields are the v7
 * personal-profile told-rows (epic #123, Phase 3). They round-trip
 * via `PATCH /api/me` and surface on `/profile`; Firebase Auth holds
 * the canonical `displayName` (`getAuth().updateUser`) while these
 * fields live on the user-doc mirror.
 *
 * `linkedinUrl` / `xHandle` / `personalSiteUrl` are the "Where to find
 * you online" told-row family — captured on the profile page rather
 * than at onboarding-time. The per-user `linkedinUrl` is preferred
 * over `BusinessContext.userLinkedinUrl` (per-org) when disambiguating
 * the Gemini-grounded person enrichment, since the same person can
 * belong to multiple orgs but has one LinkedIn identity.
 */
export const UserProfile = z.object({
    uid: z.string(),
    email: z.string().email().nullable(),
    displayName: z.string().nullable(),
    providerId: z.string(),
    emailVerified: z.boolean(),
    /** Active org for the user — clients default `X-Org-Id` to this on bootstrap. */
    primaryOrgId: z.string().nullable().optional(),
    /**
     * Denormalized list of orgIds this user belongs to. Server keeps it in
     * sync on membership changes; cheap read for "my orgs" UI without a
     * collection-group query across every org's members subcollection.
     */
    memberships: z.array(z.string()).default([]),
    /** True iff the user has the platform superAdmin claim. Mirrored here for admin listings. */
    isSuperAdmin: z.boolean().default(false),
    /** Free-text "Role / Title" the user types on their personal profile. */
    title: z.string().max(120).optional(),
    /** IANA timezone string ("America/Los_Angeles"). Free-text in the v7
     *  told row; future onboarding could narrow with an IANA picker. */
    timezone: z.string().max(80).optional(),
    /** Free-text phone number; rendered as-is (we don't normalize). */
    phone: z.string().max(40).optional(),
    /** Free-text "City, region". */
    location: z.string().max(120).optional(),
    /**
     * Public LinkedIn profile URL — primary disambiguator for the
     * Gemini-grounded person enrichment that drives `/me/learned-facts`.
     * Per-user, so it survives org switches; the route falls through to
     * `BusinessContext.userLinkedinUrl` only when this is unset.
     */
    linkedinUrl: z.string().url().max(500).optional(),
    /** Public X / Twitter handle (`@emilyreyes`) or full URL. Free-text
     *  cap matches the LinkedIn URL — we don't strip the `@` server-side
     *  so the row reads truthfully against what the user typed. */
    xHandle: z.string().max(120).optional(),
    /** Personal site URL — feeds the future welcome-note + enrichment
     *  disambiguation. Free-text, validated as a URL on the wire. */
    personalSiteUrl: z.string().url().max(500).optional(),
    /**
     * "How LeanOpx shows up for you" preferences — agent voice + briefing
     * cadence + autonomy level + quiet hours. Per-user, written via
     * PATCH /me. The org-wide `AgentSettings` in `settings/agent-settings.ts`
     * stays as the cross-team default; these are the personal overrides.
     */
    agentPreferences: UserAgentPreferences.optional(),
    /**
     * "Where to find you" — channel preferences. Email enabled by default
     * (Firebase Auth already holds the address); Slack / SMS / Push are
     * optional until the device-registration pipeline lands.
     */
    notifications: UserNotificationChannels.optional(),
    /**
     * Soft-delete sentinel. Set by `DELETE /api/me`; the auth guard treats
     * any value here as "session forbidden" and the doc is retained so an
     * admin restore is possible. We never hard-delete user docs per
     * CLAUDE.md.
     */
    deletedAt: z.string().datetime().nullable().optional(),
});
/**
 * Body for `PATCH /api/me` — v7 personal profile told-row write-through
 * (epic #123, Phase 3). Every field optional; the handler runs the
 * minimal-required updates against Firebase Auth (`displayName`) +
 * Firestore mirror (everything else). Validated free-text caps mirror
 * the `UserProfile` schema so a future field promotion is a one-line
 * change here.
 *
 * `email` is intentionally NOT mutable here — that flows through the
 * Firebase Auth verification cycle (`verify-email` route family) so
 * the rules layer's email-pinned invite gate doesn't get bypassed.
 */
export const UpdateMeRequest = z.object({
    displayName: z.string().min(1).max(120).optional(),
    title: z.string().max(120).optional(),
    timezone: z.string().max(80).optional(),
    phone: z.string().max(40).optional(),
    location: z.string().max(120).optional(),
    linkedinUrl: z.string().url().max(500).optional(),
    xHandle: z.string().max(120).optional(),
    personalSiteUrl: z.string().url().max(500).optional(),
    agentPreferences: UserAgentPreferences.optional(),
    notifications: UserNotificationChannels.optional(),
});
/**
 * Wire shape for `GET /api/me` — read-side counterpart to
 * `UpdateMeRequest`. Returns the authoritative `displayName` + `email`
 * from Firebase Auth alongside the secondary told fields from the
 * `users/{uid}` Firestore mirror, so the personal-profile page can
 * hydrate told rows on first paint after a refresh (without this, the
 * patched `title`/`timezone`/`phone`/`location` rows would read as
 * empty until the page is re-loaded with the mirror reflected back
 * through some other channel).
 */
export const GetMeResponse = z.object({
    uid: z.string(),
    email: z.string().email().nullable(),
    displayName: z.string().nullable(),
    title: z.string().max(120).optional(),
    timezone: z.string().max(80).optional(),
    phone: z.string().max(40).optional(),
    location: z.string().max(120).optional(),
    linkedinUrl: z.string().url().max(500).optional(),
    xHandle: z.string().max(120).optional(),
    personalSiteUrl: z.string().url().max(500).optional(),
    agentPreferences: UserAgentPreferences.optional(),
    notifications: UserNotificationChannels.optional(),
});
/**
 * Wire shape for `POST /api/me/reset-onboarding`. Wipes the per-org
 * `BusinessContext.onboardingCompleted` for the active org so the next
 * portal load re-enters the wizard. Returns the orgIds that were reset
 * so the client can confirm the action affected the expected scope.
 */
export const ResetOnboardingResponse = z.object({
    ok: z.literal(true),
    resetOrgIds: z.array(z.string()),
});
